/**
 * Convention Access API client
 *
 * MAESTRO:CONACCESS P3 + P4 — per MDM-CONACCESS-001 Section 6 + 6.5 + 9 + 10.
 * Wraps the /api/convention-access and /api/admin/convention-mode endpoints.
 */

import { fetchAPI } from './api.js';

// ---------------------------------------------------------------------------
// Public (unauthenticated) endpoints
// ---------------------------------------------------------------------------

/**
 * Fetch the current homepage banner state.
 * Returns {active: bool, venue_name?, venue_address?, convention_id?,
 * convention_name?, ends_at?, grace_expires_at?, mode?}.
 */
export async function getBannerState() {
  const res = await fetchAPI('/convention-access/banner-state');
  if (!res.ok) {
    return { active: false };
  }
  return res.json();
}

/**
 * Fetch the authenticated user's effective limits (tier + monthly remaining +
 * burst ceilings when a convention session or pass is active).
 */
export async function getMyEffectiveLimits() {
  const res = await fetchAPI('/convention-access/status');
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || 'Failed to fetch effective limits');
  }
  return res.json();
}

// ---------------------------------------------------------------------------
// Admin endpoints (require is_admin user)
// ---------------------------------------------------------------------------

export async function getConventionModeStatus() {
  const res = await fetchAPI('/admin/convention-mode/status');
  if (!res.ok) {
    throw new Error('Failed to fetch Convention Mode status');
  }
  return res.json();
}

export async function activateConventionMode(payload) {
  const res = await fetchAPI('/admin/convention-mode/activate', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = body?.detail || {};
    throw new Error(detail.message || `Activation failed (HTTP ${res.status})`);
  }
  return res.json();
}

export async function deactivateConventionMode(reason) {
  const res = await fetchAPI('/admin/convention-mode/deactivate', {
    method: 'POST',
    body: JSON.stringify(reason ? { reason } : {}),
  });
  if (!res.ok) {
    throw new Error('Deactivation failed');
  }
  return res.json();
}

export async function correctActiveSession(payload) {
  const res = await fetchAPI('/admin/convention-mode/correct', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail?.message || 'Correction failed');
  }
  return res.json();
}

export async function extendActiveSession(extendMinutes) {
  const res = await fetchAPI('/admin/convention-mode/extend', {
    method: 'POST',
    body: JSON.stringify({ extend_minutes: extendMinutes }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail?.message || 'Extension failed');
  }
  return res.json();
}

export async function linkSubjectToSession(subjectType, subjectRef) {
  const res = await fetchAPI('/admin/convention-mode/link-subject', {
    method: 'POST',
    body: JSON.stringify({ subject_type: subjectType, subject_ref: subjectRef }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail?.message || 'Subject linkage failed');
  }
  return res.json();
}

export async function listSessions({ limit = 50, offset = 0 } = {}) {
  const res = await fetchAPI(
    `/admin/convention-mode/sessions?limit=${limit}&offset=${offset}`
  );
  if (!res.ok) {
    throw new Error('Failed to fetch sessions');
  }
  return res.json();
}

export async function getSessionTelemetry(sessionId) {
  const res = await fetchAPI(
    `/admin/convention-mode/sessions/${sessionId}/telemetry`
  );
  if (!res.ok) {
    throw new Error('Failed to fetch telemetry');
  }
  return res.json();
}

export async function scheduleActivation(payload) {
  const res = await fetchAPI('/admin/convention-mode/schedule', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail?.message || 'Schedule failed');
  }
  return res.json();
}

export async function cancelScheduledActivation(scheduledId) {
  const res = await fetchAPI(`/admin/convention-mode/schedule/${scheduledId}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail?.message || 'Cancel failed');
  }
  return res.json();
}

export async function listScheduledActivations() {
  const res = await fetchAPI('/admin/convention-mode/schedule');
  if (!res.ok) {
    throw new Error('Failed to fetch scheduled activations');
  }
  return res.json();
}

/**
 * P4 — Issue today's signed QR token + printable PNG for the active session.
 * Returns {token, jti, iat, exp, redemption_url, qr_image_data_url, convention_session_id}.
 */
export async function getQRToken() {
  const res = await fetchAPI('/admin/convention-mode/qr-token');
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail?.message || 'Failed to issue QR token');
  }
  return res.json();
}

/**
 * P4 — Public QR redemption endpoint (Section 6 + 6.5).
 * Accepts `{token, user_lat, user_lon, reported_accuracy_m, fingerprint_hash?}`.
 * On success returns `{accepted, convention_session_id, subject_id, venue_name,
 * haversine_distance_m, grant_expires_at, grace_expires_at, welcome_message}`
 * and the server sets the `convention_access` cookie (HttpOnly, 4-hour TTL).
 *
 * On failure throws an Error with a `rejection_reason` property mapped from
 * the structured `detail.rejection_reason` field, so the caller can render
 * the friendly UX message.
 */
/**
 * P5d — Admin: list Weekend Passes awaiting fulfillment.
 */
export async function listPendingFulfillment({ limit = 50, offset = 0 } = {}) {
  const res = await fetchAPI(
    `/admin/convention-passes/pending-fulfillment?limit=${limit}&offset=${offset}`,
  );
  if (!res.ok) {
    throw new Error('Failed to fetch pending fulfillment passes');
  }
  return res.json();
}

/**
 * P5d — Admin: mark a Weekend Pass shipped + record tracking number/carrier.
 */
export async function markPassShipped(passId, { trackingNumber, trackingCarrier }) {
  const res = await fetchAPI(`/admin/convention-passes/${passId}/mark-shipped`, {
    method: 'POST',
    body: JSON.stringify({
      tracking_number: trackingNumber,
      tracking_carrier: trackingCarrier,
    }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail?.message || 'Failed to mark pass shipped');
  }
  return res.json();
}

/**
 * P5d — Admin: mark a shipped Weekend Pass as delivered.
 */
export async function markPassDelivered(passId) {
  const res = await fetchAPI(`/admin/convention-passes/${passId}/mark-delivered`, {
    method: 'POST',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail?.message || 'Failed to mark pass delivered');
  }
  return res.json();
}

/**
 * P5d — Admin: mark a Weekend Pass returned (RTS or recipient refusal).
 */
export async function markPassReturned(passId) {
  const res = await fetchAPI(`/admin/convention-passes/${passId}/mark-returned`, {
    method: 'POST',
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail?.message || 'Failed to mark pass returned');
  }
  return res.json();
}

/**
 * P7 — Public: reactive CAPTCHA state for the signup form.
 * Returns `{required, provider, site_key}`. Defaults to `{required: false}`
 * when the API is unreachable so signup is never blocked by network noise.
 */
export async function getCaptchaState() {
  const res = await fetchAPI('/auth/captcha-state');
  if (!res.ok) {
    return { required: false };
  }
  return res.json();
}

/**
 * P7 — Public: verify a CAPTCHA solution token server-side.
 */
export async function verifyCaptchaToken(token) {
  const res = await fetchAPI('/auth/verify-captcha', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail?.message || 'CAPTCHA verification request failed');
  }
  return res.json();
}

/**
 * P7 — Admin: post-con audit anomaly report (Section 6 + 6.5).
 */
export async function getPostConAuditReport(conventionId) {
  const res = await fetchAPI(`/admin/conventions/${conventionId}/post-con-audit`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail?.message || 'Failed to fetch post-con audit report');
  }
  return res.json();
}

/**
 * P6 — Admin: per-convention ROI dashboard payload (Section 12 + 12.5).
 */
export async function getConventionROIDashboard(conventionId) {
  const res = await fetchAPI(`/admin/conventions/${conventionId}/roi`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail?.message || 'Failed to fetch ROI dashboard');
  }
  return res.json();
}

/**
 * P6 — Public: post-session banner state for the authenticated user (Section 10).
 */
export async function getPostSessionBanner() {
  const res = await fetchAPI('/convention-access/post-session-banner');
  if (!res.ok) {
    return { visible: false };
  }
  return res.json();
}

/**
 * P6 — Public: record post-session banner telemetry (impression / click / dismissed).
 */
export async function recordPostSessionBannerEvent({ event, conventionSessionId }) {
  const res = await fetchAPI('/convention-access/post-session-banner/event', {
    method: 'POST',
    body: JSON.stringify({
      event,
      convention_session_id: conventionSessionId,
    }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.detail?.message || 'Failed to record banner event');
  }
  return true;
}

export async function redeemQRToken({ token, userLat, userLon, accuracyM, fingerprintHash }) {
  const headers = {};
  if (fingerprintHash) {
    headers['X-Maestro-Fingerprint-Hash'] = fingerprintHash;
  }
  const res = await fetchAPI('/convention-access/redeem', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      token,
      user_lat: userLat,
      user_lon: userLon,
      reported_accuracy_m: Math.round(accuracyM),
      fingerprint_hash: fingerprintHash || null,
    }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = body?.detail || {};
    const err = new Error(detail.message || `Redemption failed (HTTP ${res.status})`);
    err.rejection_reason = detail.rejection_reason || detail.error_code || 'unknown';
    err.status = res.status;
    throw err;
  }
  return res.json();
}
